import axios, { AxiosResponse } from 'axios';

import config from '../../../../config/config';
import { CreateMembershipModel } from '../../../../models/types/CreateMembershipModel';
import { IMembershipDates } from '../../../../models/MembershipDates';
import { IAddMembershipSummaryModel } from '../../../../models/interfaces/Membership/AddMembershipSummaryModel';
import {
  prepareQueryParams,
  QueryParamItem,
} from '../../../../utils/validations/queryParamsHelpers';
import { IKEMembershipModel } from '../../../../models/interfaces/Membership/KidnapAndExtortion/KEMembershipModel';
import { IUpdateKEMembershipOptionsModel } from '../../../../models/interfaces/Membership/KidnapAndExtortion/UpdateKEMembershipOptionsModel';
import { ICreateSecondaryMemberModel } from '../../../../models/interfaces/Membership/ICreateSecondaryMemberModel';
import {
  IResendWelcomeEmailData,
  IResendWelcomeEmailModel,
} from '../../../../models/interfaces/ResendWelcomeEmailModel';
import { MEMBERSHIP_ACTIVE_RATIFICATION_PARAM } from './const';
import { IMemberListItem } from '../../../../models/MemberListItem';
import { IPageListModel } from '../../../../models/PageListModel';

export const createKEOrganizationMembership = (
  organizationId: string,
  subscriptionId: string,
  data: CreateMembershipModel,
): Promise<AxiosResponse<string>> =>
  axios.post(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/memberships`,
    data,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const analyzeKECreateMembership = (
  data: CreateMembershipModel,
): Promise<AxiosResponse> =>
  axios.post(`api/memberships/newmembershipanalysis`, data, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
  });

export const getKEMembershipSummary = (
  organizationId: string,
  subscriptionId: string,
  membershipPlanId: string,
): Promise<AxiosResponse<IAddMembershipSummaryModel>> =>
  axios.get(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/membershipPlans/${membershipPlanId}/summary`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const getDatesForNewKEMemberships = (
  subscriptionId?: string,
  cancellationToken?: AbortSignal,
): Promise<AxiosResponse<IMembershipDates>> => {
  const queryParams: QueryParamItem[] = [
    {
      name: 'subscriptionId',
      value: subscriptionId,
    },
  ];
  const params = prepareQueryParams({ queryParams });
  return axios.get(`api/memberships/newmembershipdates${params}`, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
    signal: cancellationToken,
  });
};

export const getKERetailMembershipPlanSummary = (
  membershipPlanId: string,
  promoCode: string,
  effectiveDate: string,
  autoRenewal?: boolean,
  cancellationToken?: AbortSignal,
): Promise<AxiosResponse<IAddMembershipSummaryModel>> => {
  const queryParams: QueryParamItem[] = [
    {
      name: 'membershipPlanId',
      value: membershipPlanId,
    },
    {
      name: 'promoCode',
      value: promoCode,
    },
    {
      name: 'effectiveDate',
      value: effectiveDate,
    },
    {
      name: 'autoRenewal',
      value: autoRenewal,
    },
  ];
  const params = prepareQueryParams({ queryParams });
  return axios.get(`api/memberships/summary${params}`, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
    signal: cancellationToken,
  });
};

export const getKEExistingMembershipSummary = (
  membershipId: string,
): Promise<AxiosResponse<IAddMembershipSummaryModel>> =>
  axios.get(`api/memberships/${membershipId}/summary`, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
  });

export const getKEMembershipDetails = (
  membershipId: string,
): Promise<AxiosResponse<IKEMembershipModel>> =>
  axios.get(`api/memberships/${membershipId}`, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
  });

export const putKEMemberToBeRenewed = (
  membershipId: string,
  memberId: string,
  toBeRenewed: boolean,
): Promise<AxiosResponse> =>
  axios.put(
    `api/memberships/${membershipId}/members/${memberId}`,
    { toBeRenewed },
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const updateKEMembershipOptionsRequest = (
  membershipId: string,
  options: IUpdateKEMembershipOptionsModel,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<IKEMembershipModel>> =>
  axios.put(`api/memberships/${membershipId}/options`, options, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
    signal: cancellationToken,
  });

export const addKESecondaryMemberRequest = (
  membershipId: string,
  data: ICreateSecondaryMemberModel,
): Promise<AxiosResponse> =>
  axios.post(`api/memberships/${membershipId}/members`, data, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
  });

export const deleteKESecondaryMemberRequest = (
  membershipId: string,
  memberId: string,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<void>> =>
  axios.delete(`api/memberships/${membershipId}/secondarymember/${memberId}`, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
    signal: cancellationToken,
  });

export const postResendKEWelcomeEmail = (
  membershipId: string,
  memberId: string,
  data: IResendWelcomeEmailData,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<IResendWelcomeEmailModel>> =>
  axios.post(
    `api/memberships/${membershipId}/members/${memberId}/welcomeEmail`,
    data,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
      signal: cancellationToken,
    },
  );

export const deleteKEMembershipRequest = (
  membershipId: string,
  isActive: boolean,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<void>> =>
  axios.delete(
    `api/memberships/${membershipId}${
      isActive ? MEMBERSHIP_ACTIVE_RATIFICATION_PARAM : ''
    }`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
      signal: cancellationToken,
    },
  );

export const getKEMembershipsMembers = (
  organizationId: string,
  params?: string,
  cancellationToken?: AbortSignal,
): Promise<AxiosResponse<IPageListModel<IMemberListItem>>> => {
  const queryParams: QueryParamItem[] = [
    { name: 'organizationId', value: organizationId },
  ];

  const currentParams = prepareQueryParams({
    initQueryString: params,
    queryParams,
  });

  return axios.get(`api/members${currentParams}`, {
    baseURL: config.kidnapAndExtortionApi.baseUrl,
    signal: cancellationToken,
  });
};
